import { Box, Card, Stack, Typography, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { tokens } from "../../../theme";

const amenityLabels: Record<string, { label: string; icon: React.ReactNode }> =
  {
    internet: {
      label: "High-Speed Internet",
      icon: <WifiOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    power: {
      label: "Power Continuity",
      icon: <BoltOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    catering: {
      label: "On-Site Production Space",
      icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} />,
    },
  };

interface VenueInfrastructurePanelProps {
  venue: {
    venueName: string;
    capacity: number;
    amenities: string[];
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export default function VenueInfrastructurePanel({
  venue,
}: VenueInfrastructurePanelProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: cardBg,
        borderRadius: "18px",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: textPrimary, mb: 2 }}
        >
          Venue & Broadcast Infrastructure
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            {[
              { label: "Venue Name", value: venue.venueName },
              {
                label: "In-Person Capacity",
                value: venue.capacity.toLocaleString(),
              },
              {
                label: "Coordinates",
                value: `${venue.location.latitude}, ${venue.location.longitude}`,
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.9,
                  borderBottom: `1px solid ${textMuted}12`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: textMuted }}>
                  {item.label}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                color: textMuted,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1.25,
              }}
            >
              Confirmed Amenities
            </Typography>
            <Stack spacing={1}>
              {venue.amenities.map((id) => {
                const amenity = amenityLabels[id];
                if (!amenity) return null;
                return (
                  <Box
                    key={id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      bgcolor: "#4BA66314",
                      borderRadius: "10px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box sx={{ color: "#4BA663" }}>{amenity.icon}</Box>
                    <Typography
                      sx={{ fontSize: 13, color: textPrimary, fontWeight: 500 }}
                    >
                      {amenity.label}
                    </Typography>
                    <CheckIcon
                      sx={{ fontSize: 14, color: "#4BA663", ml: "auto" }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            borderRadius: "14px",
            overflow: "hidden",
            minHeight: 180,
            flex: 1,
            bgcolor: mutedCard,
            border: `1px solid ${textMuted}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <iframe
            title="venue-map"
            src={`https://maps.google.com/maps?q=${venue.location.latitude},${venue.location.longitude}&z=15&output=embed`}
            width="100%"
            height="100%"
            style={{
              border: 0,
              display: "block",
              position: "absolute",
              top: 0,
              left: 0,
            }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      </Box>
    </Card>
  );
}
